<template>
  <div class="col-span-12 lg:col-span-8 2xl:col-span-9">
    <div class="intro-y box lg:mt-5">
      <div
        class="flex items-center p-5 border-b border-slate-200/60 dark:border-darkmode-400"
      >
        <h2 class="mr-auto text-base font-medium">{{ $t("personal_information") }}</h2>
      </div>

      <div class="p-5">
        <div class="flex flex-col xl:flex-row">
          <form class="flex-1 mt-6 xl:mt-0" @submit.stop.prevent="onUpdateUser">
            <FieldsFormDisplay
              :collection="collection!"
              :item="props.profileData"
              :fields="systemFields"
              v-on="systemFieldsListeners"
            />

            <FieldsFormDisplay
              :collection="collection!"
              :item="props.profileData"
              :fields="customFields"
              v-on="customFieldsListeners"
            />

            <Button
              v-tooltip.top="{
                value: $t('error.not_enough_permission.to_update'),
                disabled: havePermissionForUpdate,
                context: {
                  right: true,
                },
              }"
              variant="primary"
              type="submit"
              :disabled="!canSubmitForm"
              class="w-24 mt-3"
              >{{ $t("save") }}
            </Button>

            <ValidationErrorDetailsTippyButton
              :isShown="!canSubmitForm"
              :errors="validationErrors"
              class="ml-3"
            />
          </form>

          <div
            :class="{ 'items-center': !props.profileData.data.avatar }"
            class="mx-auto w-52 max-h-[200px] zoom-in rounded-md border-2 border-dashed shadow-sm flex justify-center xl:mr-0 xl:ml-6 border-slate-200/60 dark:border-darkmode-400"
          >
            <ImageInterface
              ref="refAvatarUploader"
              :class="{
                'pt-4': props.profileData.data.avatar,
                '-mt-6': !props.profileData.data.avatar,
              }"
              :collection="collection!"
              :item="props.profileData"
              :field="avatarField.avatar"
              v-on="customFieldsListeners"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from "#i18n";
  import { computed, toRef, ref } from "vue";
  import { IUser, UsersGatewayInterface } from "~/entities/user";
  import Button from "~/shared/ui/Button";
  import { iocContainer } from "~/inversify.config";
  import { INJECT_SYMBOLS } from "~/service/inversion-of-control/inject-symbols";
  import { toaster } from "~/service/toaster";
  import ImageInterface from "~/api/field-interfaces/Image/ImageInterface.vue";
  import {
    getSystemFieldList,
    FieldsFormDisplay,
    useFieldsEventListeners,
    useCollectionFieldsExcluding,
    useCollectionFieldsIncluding,
    IField,
  } from "~/entities/field";
  import { useCollecitonsStore } from "~/stores/collections";
  import { usePermissionsStore } from "~/stores/permissions";
  import { PermissionAction } from "~/api/permissions/entity/PermissionInterface";
  import { useItemValidation } from "~/entities/item-validation";
  import { ValidationErrorDetailsTippyButton } from "~/entities/item-validation";

  interface Props {
    profileData: IUser;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    "update:user": [payload: IUser];
  }>();

  const collectionsStore = useCollecitonsStore();
  const permissionsStore = usePermissionsStore();
  const { t } = useI18n();

  const refAvatarUploader = ref();

  const collection = computed(() => collectionsStore.getCollection("directus_users"));

  const havePermissionForUpdate = computed<boolean>(() =>
    permissionsStore.hasCollectionPermission("directus_users", PermissionAction.UPDATE),
  );

  const computedProfileData = computed(() => {
    return props.profileData;
  });

  const { fields: systemFields } = useCollectionFieldsIncluding(toRef("directus_users"), [
    "first_name",
    "last_name",
    "email",
  ]);

  const { fields: avatarField } = useCollectionFieldsIncluding(toRef("directus_users"), [
    "avatar",
  ]);

  const { fieldsEventListeners: systemFieldsListeners } =
    useFieldsEventListeners(computedProfileData);

  const { fields: customFields } = useCollectionFieldsExcluding(
    toRef("directus_users"),
    getSystemFieldList("directus_users"),
  );
  const { fieldsEventListeners: customFieldsListeners } =
    useFieldsEventListeners(computedProfileData);

  async function onUpdateUser(): Promise<void> {
    try {
      const userApi = iocContainer.get<UsersGatewayInterface>(
        INJECT_SYMBOLS.UsersGatewayInterface,
      );

      await refAvatarUploader.value.finalize();

      const updatedUser = await userApi.updateUser(
        props.profileData.id as string,
        props.profileData,
      );

      toaster().success(t("user_updated_message"));
      emit("update:user", updatedUser);
    } catch (err) {
      // @ts-expect-error
      toaster().error(`${t("error_user_update")}: ${err!.message}`);
    }
  }

  const availableFieldsInfo = computed<IField[]>(() =>
    Object.values(systemFields.value)
      .map((field) => field)
      .concat(Object.values(customFields.value).map((field) => field)),
  );

  const { isValid, errors: validationErrors } = useItemValidation(
    computed(() => props.profileData),
    availableFieldsInfo,
  );

  const canSubmitForm = computed<boolean>(
    () => !!isValid.value && !!havePermissionForUpdate.value,
  );
</script>
